import { useEffect, useState } from 'react'
import { Formik, FormikValues } from 'formik'
import * as yup from 'yup'
import styles from './AuthPage.module.css'
import s from '../../styles/common/Form.module.css'
import CustomInput from '../../components/UI/CustomInput/CustomInput'
import CustomButton from '../../components/UI/CustomButton/CustomButton'
import usePasswordToggle from '../../hooks/usePasswordToggle'
import HeaderForAuth from '../../components/HeaderForAuth/HeaderForAuth'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import PATH from '../../navigation/path'
import { useAppDispatch, useAppSelector } from '../../redux/types/types'
import { loginUser } from '../../redux/actions/authAction'
import Loader from '../../components/UI/Loader/Loader'
import { waitTimer } from '../../utils/waitTimer'
import mainBackground from '../../assets/img/mainBackground.png'
import { clearError } from '../../redux/reducers/auth/authSlice'
import { setCookie } from 'nookies'

const AuthPage = () => {
  const [PasswordInputType, ToggleIcon] = usePasswordToggle()
  const [submittedValues, setSubmittedValues] = useState<any>(null)

  const { isLoading, error } = useAppSelector((state) => state.auth)

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const userDataParam = searchParams.get('userData')

    if (userDataParam) {
      try {
        const userData = JSON.parse(decodeURIComponent(userDataParam))

        setSubmittedValues({
          login: userData.email,
          password: userData.password,
        })
      } catch (error) {
        console.error('Ошибка при парсинге данных пользователя:', error)
      }
    }
  }, [searchParams])

  const validationSchema = yup.object().shape({
    login: yup
      .string()
      .transform((value) => value.trim())
      .required('Поле не может быть пустым'),
    password: yup
      .string()
      .transform((value) => value.trim())
      .required('Пароль не может быть пустым'),
  })

  useEffect(() => {
    const accessToken = searchParams.get('access')

    const refreshToken = searchParams.get('refreshToken')

    if (accessToken && refreshToken) {
      setCookie(null, 'authToken', accessToken, {
        maxAge: 30 * 24 * 60 * 60, // 30 days
        path: '/',
      })
      setCookie(null, 'refreshToken', refreshToken, {
        maxAge: 30 * 24 * 60 * 60, // 30 days
        path: '/',
      })
      navigate('/')
    }
  }, [searchParams])

  const handleSubmitHandler = async (values: FormikValues) => {
    setSubmittedValues(values)
    const resultAction = await dispatch(
      loginUser({ email: values.login.trim(), password: values.password.trim() }),
    )

    if (loginUser.rejected.match(resultAction)) {
      waitTimer(() => dispatch(clearError()))
    } else {
      navigate('/claim-management-page/')
    }
  }

  useEffect(() => {
    if (error === 'Ваш аккаунт заблокирован') {
      dispatch(clearError())
      navigate(PATH.blockedPage)
    }
  }, [error])

  if (isLoading) {
    return <Loader />
  }

  return (
    <div
      className={styles.authPage}
      style={{
        background: `linear-gradient(90deg, rgba(0, 0, 0, 0.80) 0%, rgba(0, 0, 0, 0.00) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.60) 100%), url(${mainBackground}), lightgray 50% / cover no-repeat`,
      }}
    >
      <div className={styles.container}>
        <HeaderForAuth />
        <div className={styles.formContentBlock}>
          <div className={s.leftBlock}>
            <h1 className={s.whiteTitle}>Рады видеть вас!</h1>
            <Formik
              key={submittedValues ? 'with-data' : 'without-data'}
              initialValues={
                submittedValues || {
                  login: '',
                  password: '',
                }
              }
              validateOnBlur
              onSubmit={(values) => handleSubmitHandler(values)}
              validationSchema={validationSchema}
              enableReinitialize={true}
            >
              {(props) => {
                const { values, touched, errors, isValid, handleChange, handleBlur, handleSubmit } =
                  props

                return (
                  <div className={styles.formContainer}>
                    <div className={styles.inputGroup}>
                      <h4 className={s.whiteSubtitle}>Логин</h4>
                      <CustomInput
                        autoComplete={'new-text'}
                        type='text'
                        placeholder='Введите'
                        name='login'
                        required
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.login}
                        className={errors.login && touched.login ? s.inputError : s.input}
                      />

                      {touched.login && errors.login && (
                        <div className={s.errorContainer}>
                          <p className={s.errorValidation}>{errors.login as string}</p>{' '}
                        </div>
                      )}
                      <div className={styles.passwordContainer}>
                        <h4 className={s.whiteSubtitle}>Пароль</h4>
                        <div className={styles.passInput}>
                          <CustomInput
                            autoComplete={'new-password'}
                            type={PasswordInputType}
                            placeholder='Пароль'
                            name='password'
                            required
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.password}
                            className={errors.password && touched.password ? s.inputError : s.input}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && isValid) {
                                e.preventDefault()
                                handleSubmit()
                              }
                            }}
                          />
                          <div className={styles.eyeIcon}>{ToggleIcon}</div>
                        </div>
                        {touched.password && errors.password && (
                          <div className={s.errorContainer}>
                            <p className={s.errorValidation}>{errors.password as string}</p>{' '}
                          </div>
                        )}
                        {error && <p className={s.errorValidation}>{error}</p>}
                      </div>
                      <div className={styles.passwordRecoveryBlock}>
                        <Link to={PATH.passwordRecoveryPage} className={styles.passwordLinkWhite}>
                          Забыли пароль ?
                        </Link>
                      </div>
                    </div>
                    <div className={styles.buttonBlock}>
                      <CustomButton
                        title='К регистрации'
                        disabled={false}
                        onClick={() => navigate(PATH.registrationPage)}
                        type='submit'
                        className={styles.regButton}
                      />
                      <CustomButton
                        title='Войти'
                        disabled={!isValid}
                        onClick={() => handleSubmit()}
                        type='submit'
                        className={styles.submitBtn}
                      />
                      {/* <Link to={PATH.registrationPage} className={styles.passwordLinkWithoutLine}>
                        К регистрации
                      </Link> */}
                    </div>
                  </div>
                )
              }}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AuthPage
